/**
 * ORG-EDITOR.1+2 (2026-06-04): українські класифікатори для OrganizationEditor.
 *
 * 1. OPFG_CODES — Класифікатор організаційно-правових форм господарювання
 *    ДК 002:2004. Найпоширеніші ~30 кодів (повний — 80+, рідкісні відсутні).
 *
 * 2. IBAN bank lookup — НЕ hardcoded мапа (як було у MVP), а через
 *    довідник у БД `ref.banks` (seed з NBU API, 58 активних банків).
 *    Endpoint: GET /refs/banks. Frontend кешує через React Query.
 *
 *    Алгоритм: український IBAN формат "UA__ XXXXXX YYYYYYYYYYYYYYYYYYYY"
 *    де XXXXXX (6 цифр з позиції 5-10 — МФО банку, він же ID у NBU).
 *    Приклад: UA713395000000026008370230001 → МФО=339500 (ТАСКОМБАНК).
 *
 *    Власник 2026-06-04: hardcoded TS-таблиця була **помилковою** —
 *    я приписав 339500 = Мегабанк (реально ТАСКОМБАНК), 300012 =
 *    monobank (реально ліквідований Промінвест). Заміна на DB-driven
 *    джерело з оновленням через NBU API.
 */

export interface BankRow {
  mfo: string;
  name: string;
  edrpou?: string | null;
}

export interface OpfgOption {
  code: string;
  name: string;
}

/** ДК 002:2004 — топ-30 найпоширеніших ОПФГ. */
export const OPFG_CODES: OpfgOption[] = [
  // ТОВ і похідні
  { code: '240', name: 'Товариство з обмеженою відповідальністю (ТОВ)' },
  { code: '241', name: 'Товариство з додатковою відповідальністю' },
  // АТ
  { code: '230', name: 'Приватне акціонерне товариство (ПрАТ)' },
  { code: '231', name: 'Публічне акціонерне товариство (ПАТ)' },
  // ПП
  { code: '320', name: 'Приватне підприємство (ПП)' },
  // Кооперативи
  { code: '340', name: 'Виробничий кооператив' },
  { code: '350', name: 'Споживчий кооператив' },
  { code: '360', name: 'Сільськогосподарський кооператив' },
  // Державні і комунальні
  { code: '410', name: 'Державне підприємство' },
  { code: '420', name: 'Казенне підприємство' },
  { code: '430', name: 'Комунальне підприємство' },
  // ФОП
  { code: '280', name: 'Фізична особа-підприємець (ФОП)' },
  // Філії, представництва
  { code: '590', name: 'Філія (інший відокремлений підрозділ)' },
  { code: '610', name: 'Представництво' },
  // Громадські, релігійні
  { code: '815', name: 'Громадська організація' },
  { code: '820', name: 'Релігійна організація' },
  { code: '830', name: 'Профспілка' },
  { code: '845', name: 'Благодійна організація' },
  // Iнше
  { code: '425', name: 'Орган виконавчої влади' },
  { code: '440', name: 'Орган місцевого самоврядування' },
  { code: '910', name: 'Iноземне підприємство' },
  { code: '935', name: 'Спільне підприємство' },
];

/** Знайти OPFG-name за code. */
export function findOpfgName(code: string | null | undefined): string | null {
  if (!code) return null;
  const entry = OPFG_CODES.find((o) => o.code === code);
  return entry ? entry.name : null;
}

/**
 * Витягти МФО з українського IBAN. Формат UA__ MFO(6) ACCOUNT(19).
 * Повертає null якщо IBAN некоректний або не український.
 */
export function extractMfoFromIban(iban: string | null | undefined): string | null {
  if (!iban) return null;
  const clean = iban.replace(/\s+/g, '').toUpperCase();
  // UA + 2 контр.цифри + 6 цифр МФО + 19 цифр рахунку = 29 символів
  if (!/^UA\d{27}$/.test(clean)) return null;
  return clean.slice(4, 10);
}

/**
 * Знайти назву банку за IBAN, використовуючи список банків з БД.
 * NULL якщо невідомий банк / некоректний IBAN / список ще не завантажено.
 */
export function findBankByIban(
  iban: string | null | undefined,
  banks: BankRow[],
): string | null {
  const mfo = extractMfoFromIban(iban);
  if (!mfo) return null;
  const bank = banks.find((b) => b.mfo === mfo);
  return bank ? bank.name : null;
}

/**
 * Format IBAN з пробілами:  UA71 339500 00000 2600 8370 2300 01
 * Маска (НБУ-стиль): 4 (UA+КС) + 6 (МФО) + 5 + 4 + 4 + 4 + 2 = 29 символів.
 * Це звичний банк/бух-формат — бухгалтер бачить МФО окремою групою.
 */
export function formatIban(iban: string | null | undefined): string {
  if (!iban) return '';
  const clean = iban.replace(/\s+/g, '').toUpperCase();
  if (clean.length === 0) return '';
  // Якщо ще не повний IBAN — просто додаємо пробіли по 4 для зручності вводу
  if (clean.length < 29) {
    return clean.match(/.{1,4}/g)?.join(' ') ?? clean;
  }
  // Маска 4-6-5-4-4-4-2 для повного UA IBAN (29 символів)
  return [
    clean.slice(0, 4),    // UA + 2 контр.цифри (4)
    clean.slice(4, 10),   // МФО (6)
    clean.slice(10, 15),  // 5
    clean.slice(15, 19),  // 4
    clean.slice(19, 23),  // 4
    clean.slice(23, 27),  // 4
    clean.slice(27, 29),  // 2
  ].join(' ');
}

/** Валідація українського IBAN — формат UA + 27 цифр. Без mod-97 checksum. */
export function isValidUaIban(iban: string | null | undefined): boolean {
  if (!iban) return false;
  const clean = iban.replace(/\s+/g, '').toUpperCase();
  return /^UA\d{27}$/.test(clean);
}
